/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    bool, func, object, string,
} from 'prop-types';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';

// Material UI
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import StopRoundedIcon from '@material-ui/icons/StopRounded';
import { sanitizeContent } from '../../../../../../../../../../lib/sanitize';

import { trackEvent } from '../../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

// Helper

const styles = (theme) => ({
    root: {
        backgroundColor: '#fff',
        bottom: '0',
        borderTop: '1px solid #E9E9E9',
        left: '0',
        padding: '17px 57px',
        position: 'sticky',
        zIndex: 1,
        [theme.breakpoints.down(670)]: {
            padding: '15px 25px',
        },
        [theme.breakpoints.down(408)]: {
            padding: '10px',
        },
    },
    arrivalDateContainer: {
        padding: 0,
    },
    iconLegend: {
        color: '#DBEEE4',
        fontSize: '24px',
        verticalAlign: 'top',
        [theme.breakpoints.down(670)]: {
            fontSize: '20px',
        },
        [theme.breakpoints.down(408)]: {
            fontSize: '17px',
        },
    },
    title: {
        fontSize: '1em',
        display: 'block',
        verticalAlign: 'top',
        margin: '0 0 0 -6px',
        [theme.breakpoints.down(458)]: {
            fontSize: '0.88em',
        },
        [theme.breakpoints.down(412)]: {
            fontSize: '0.82em',
        },
        [theme.breakpoints.down(352)]: {
            fontSize: '0.75em',
        },
    },
    subTitle: {
        display: 'block',
        fontSize: '1em',
        fontWeight: 'bold',
        margin: '0 0 0 18px',
        width: '100%',
        [theme.breakpoints.down(670)]: {
            marginLeft: '14px',
        },
        [theme.breakpoints.down(458)]: {
            fontSize: '0.88em',
        },
        [theme.breakpoints.down(412)]: {
            fontSize: '0.82em',
        },
        [theme.breakpoints.down(408)]: {
            marginLeft: '11px',
        },
        [theme.breakpoints.down(352)]: {
            fontSize: '0.75em',
        },
    },
    buttonContainer: {
        margin: '0',
    },
    applyButton: {
        backgroundColor: theme.palette.buttonBackgroundColor,
        color: theme.palette.primary?.contrastText,
        margin: '0',
        padding: '9px 24px',
        textTransform: 'capitalize',
        fontWeight: 'bold',
        fontSize: '1em',
        width: '150px',
        boxShadow: 'none',
        [theme.breakpoints.down(408)]: {
            width: '95%',
            padding: '5px 14px',
        },
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.buttonBackgroundColorSecondary,
        },
    },
});

const FoodArrivalDetails = ({
    applyDateCallBack,
    classes,
    errorMessage,
    forceExactDay,
    isMonthDatedCalendar,
    isPassportUser,
    isWeekDatedCalendar,
    isUseCalendarDateTypeByEnabled,
    passportSubscriptionStatus,
    selectedDeliveryDate,
    selectedHoliday,
    shipHolidayDetails,
    shopRunnerLoggedIn,
}) => {
    const dispatch = useDispatch();

    const {
        calendarDateType,
        dateObj,
        deliveryDate,
        rangeStartDate,
        upcharge,
        guaranteedChargeToApply,
        surcharge,
        holidayCode,
        productSku,
    } = selectedDeliveryDate;

    let arrivalDateTitle = '';
    let arrivalDateSubtitle = 'Standard Charges';
    let arrivalDate = '';

    const totalCharges = (parseFloat(upcharge || 0, 10)) + (parseFloat(surcharge || 0, 10));
    // Figure out for the week
    // const SelDayIndex = (dateObj) ? dateObj.getDay() : '';
    // const dayOfTheWeek = DaysOfTheWeekFull[SelDayIndex];

    if (
        !forceExactDay
        && selectedHoliday && holidayCode
        && shipHolidayDetails[holidayCode]?.length > 0
    ) {
        const holidayDetails = shipHolidayDetails[holidayCode][0];
        arrivalDateTitle = `Guaranteed by ${holidayDetails.holidayDesc}`;
    } else if (isWeekDatedCalendar || isMonthDatedCalendar) {
        let delDatetoFormat = rangeStartDate || deliveryDate;

        if (delDatetoFormat?.indexOf('-') > 0) {
            delDatetoFormat = delDatetoFormat.split('-');
            delDatetoFormat = `${delDatetoFormat[1]}/${delDatetoFormat[2]}/${delDatetoFormat[0]} 00:00:00`;
        }

        let delEndDate = '';
        let delDateMonth = '';
        if  (delDatetoFormat) {
            delEndDate = format(new Date(delDatetoFormat), 'MMM. do');
            delDateMonth = format(new Date(delDatetoFormat), 'MMM yyyy');
        }

        arrivalDateTitle = `Delivery ${isWeekDatedCalendar ? 'Week' : 'Month'} of ${isWeekDatedCalendar ? delEndDate : delDateMonth}`;
    } else if (
        isUseCalendarDateTypeByEnabled
        && dateObj && calendarDateType === 'by'
    ) {
        arrivalDateTitle = `Delivery by ${format(new Date(dateObj), 'MMM. do')}`;
        arrivalDate = `${dateObj.toDateString()}`;
    } else if (
        dateObj && (calendarDateType === 'on' || forceExactDay)
    ) {
        arrivalDateTitle = `Delivery on ${format(new Date(dateObj), 'MMM. do')}`;
    } else if (
        calendarDateType === 'between'
    ) {
        const delStartDate = format(new Date(`${rangeStartDate}T00:00:00`), 'MMM. do');
        const delEndDate = format(new Date(`${deliveryDate}T00:00:00`), 'MMM. do');
        arrivalDateTitle = sanitizeContent(`Delivery ${delStartDate} &mdash; ${delEndDate}`);
    }

    if (forceExactDay && calendarDateType === 'between' && guaranteedChargeToApply) {
        arrivalDateSubtitle = `Standard Charges + $${guaranteedChargeToApply}`;
    } else if (totalCharges > 0) {
        arrivalDateSubtitle = `Standard Charges + $${totalCharges.toFixed(2)}`;
    }

    if (shopRunnerLoggedIn) {
        if (totalCharges > 0) {
            arrivalDateSubtitle = `$${totalCharges.toFixed(2)} ShopRunner shipping`;
        } else {
            arrivalDateSubtitle = 'Free ShopRunner Shipping';
        }
    }

    if (isPassportUser || passportSubscriptionStatus) {
        if (forceExactDay && calendarDateType === 'between' && guaranteedChargeToApply) {
            arrivalDateSubtitle = `$${guaranteedChargeToApply} Passport Shipping`;
        } else if (totalCharges > 0) {
            arrivalDateSubtitle = `$${totalCharges.toFixed(2)} Passport Shipping`;
        } else {
            arrivalDateSubtitle = 'Free Passport Shipping';
        }
    }

    return (
        <Grid id="food-arrival" container className={classes.root}>
            <Grid item xs={7} className={classes.arrivalDateContainer}>
                <Grid container>
                    {arrivalDateTitle && !errorMessage ? (
                        <>
                            <p id="title" className={classes.title}><StopRoundedIcon className={classes.iconLegend} />{arrivalDateTitle}</p>
                            <p id="subtitle" className={classes.subTitle}>{arrivalDateSubtitle}</p>
                        </>
                    ) : null}
                </Grid>
            </Grid>
            <Grid item xs={5} className={classes.buttonContainer}>
                <Grid id="apply-button-content" container justifyContent="flex-end">
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={!!errorMessage}
                        onClick={
                            () => {
                                applyDateCallBack();
                                dispatch(trackEvent({
                                    eventName: 'feature_interaction',
                                    eventCategory: 'Checkout',
                                    eventAction: 'Delivery Calendar',
                                    eventLabel: `${arrivalDate} | ${productSku}`,
                                }));
                                if (totalCharges > 0) {
                                    dispatch(trackEvent({
                                        eventName: 'feature_interaction',
                                        eventCategory: 'Checkout',
                                        eventAction: 'Delivery Calendar',
                                        eventLabel: `Surcharge: $${totalCharges.toFixed(2)} | ${productSku}`,
                                    }));
                                }
                            }
                        }
                        className={classes.applyButton}
                        data-test="checkout-delivery-calendar-apply-date"
                    >
                        Apply Date
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

FoodArrivalDetails.propTypes = {
    applyDateCallBack: func.isRequired,
    classes: object.isRequired,
    errorMessage: string.isRequired,
    forceExactDay: bool.isRequired,
    isMonthDatedCalendar: bool,
    isWeekDatedCalendar: bool,
    isPassportUser: bool.isRequired,
    isUseCalendarDateTypeByEnabled: bool,
    passportSubscriptionStatus: bool.isRequired,
    selectedHoliday: object.isRequired,
    selectedDeliveryDate: object.isRequired,
    shipHolidayDetails: object.isRequired,
    shopRunnerLoggedIn: string,
};

FoodArrivalDetails.defaultProps = {
    isWeekDatedCalendar: false,
    isMonthDatedCalendar: false,
    isUseCalendarDateTypeByEnabled: false,
    shopRunnerLoggedIn: '',
};

export default withStyles(styles)(FoodArrivalDetails);
